import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, SpectrumBar } from "@/components/BrandMark";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const Hero = () => (
  <div className="relative hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12 overflow-hidden">
    <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#1A73E8] opacity-90" />
    <div className="absolute right-40 top-60 w-40 h-40 rounded-full bg-[#FBBC04]" />
    <div className="absolute -left-16 bottom-32 w-64 h-64 rounded-full bg-[#34A853] opacity-90" />
    <div className="absolute right-16 bottom-16 w-24 h-24 rounded-full bg-[#EA4335]" />
    <p className="relative font-mono text-xs tracking-[0.3em] uppercase text-slate-400">Internal · Employees only</p>
    <div className="relative">
      <h1 className="font-heading text-6xl font-extrabold tracking-tight leading-[0.95]">
        Build your<br />swag kit.
      </h1>
      <p className="mt-6 max-w-sm text-slate-300 text-base leading-relaxed">
        Pick your favourite pieces, choose your colors and sizes, and we'll ship them to your door.
      </p>
    </div>
  </div>
);

const EmailStep = ({ email, setEmail, loading, onSubmit }) => (
  <form onSubmit={onSubmit} className="space-y-5" data-testid="login-email-form">
    <label className="block text-xs font-mono uppercase tracking-widest text-slate-500" htmlFor="email">Work email</label>
    <div className="relative">
      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        id="email" type="email" required autoFocus value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        data-testid="login-email-input"
        className="w-full h-14 pl-11 pr-4 rounded-xl border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#4285F4] focus:border-transparent"
      />
    </div>
    <button
      type="submit" disabled={loading} data-testid="login-send-otp-btn"
      className="w-full h-14 rounded-full bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold flex items-center justify-center gap-2 transition-colors active:scale-[0.98] disabled:opacity-60"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send me a code <ArrowRight className="w-4 h-4" /></>}
    </button>
  </form>
);

const OtpStep = ({ email, code, setCode, loading, onVerify, onResend, cooldown, onBack }) => (
  <div className="space-y-6" data-testid="login-otp-step">
    <button onClick={onBack} data-testid="login-change-email-btn" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
      <ArrowLeft className="w-4 h-4" /> Use a different email
    </button>
    <p className="text-slate-600">We sent a 6-digit code to <span className="font-semibold text-slate-900" data-testid="login-otp-email">{email}</span></p>
    <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={onVerify} autoFocus data-testid="login-otp-input">
      <InputOTPGroup className="gap-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <InputOTPSlot key={i} index={i} className="h-14 w-12 text-xl font-mono rounded-xl border border-slate-200 bg-white first:rounded-xl last:rounded-xl" />
        ))}
      </InputOTPGroup>
    </InputOTP>
    <button
      onClick={() => onVerify(code)} disabled={loading || code.length !== 6} data-testid="login-verify-otp-btn"
      className="w-full h-14 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center justify-center gap-2 transition-colors active:scale-[0.98] disabled:opacity-50"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & sign in"}
    </button>
    <button
      onClick={onResend} disabled={cooldown > 0 || loading} data-testid="login-resend-otp-btn"
      className="text-sm font-medium text-[#1A73E8] disabled:text-slate-400"
    >
      {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
    </button>
  </div>
);

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (user) return <Navigate to={user.has_order ? "/my-order" : "/"} replace />;

  const sendCode = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      setStep("otp");
      setCode("");
      setCooldown(data.resend_after);
      toast.success("Code sent — check your inbox");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (value) => {
    if (value.length !== 6 || loading) return;
    setLoading(true);
    try {
      const { token, ...data } = (await api.post("/auth/verify-otp", { email, code: value })).data;
      localStorage.setItem(TOKEN_KEY, token);
      toast.dismiss();
      setUser(data);
      navigate(data.has_order ? "/my-order" : "/");
    } catch (e) {
      toast.error(errMsg(e));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]">
      <Hero />
      <div className="flex flex-col">
        <SpectrumBar className="h-1 lg:hidden" />
        <div className="flex-1 flex items-center px-6 sm:px-12 lg:px-20 py-16">
          <div className="w-full max-w-md animate-rise">
            <BrandMark />
            <h2 className="mt-12 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Sign in</h2>
            <p className="mt-3 mb-10 text-slate-500">Only invited emails can access the store. No password needed.</p>
            {step === "email" ? (
              <EmailStep email={email} setEmail={setEmail} loading={loading} onSubmit={(e) => { e.preventDefault(); sendCode(); }} />
            ) : (
              <OtpStep
                email={email} code={code} setCode={setCode} loading={loading} onVerify={verify}
                onResend={sendCode} cooldown={cooldown} onBack={() => setStep("email")}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
